import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import * as React from "react";
import { useSelector } from "react-redux";
import LinearProgress from "@mui/material/LinearProgress";
import { NoRowOverlay } from "../../table/NoRowOverlay";
import { getOrders, getOrdersbyCenter } from "../../../redux/actions/order";
import { getOrdersbyStudy, updateOrder } from "../../../redux/actions/order";
import { store } from "../../../store";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import CSVDownloader from "../../ui/CSVDownloader";
import { isWeekend } from "date-fns";
import { showSuccessAlert } from "../../ui/utils/AlertUtils";
import CustomFilter from "../../table/CustomFilter";
import getUniqueOptions from "../../table/getUniqueOptions";
import { useTranslation } from "react-i18next";
import LocalizedDataGrid from "../../ui/datagrid/LocalizedDataGrid";
import FirstSupplyDialog from "../../ui/dialogs/FirstSupplyDialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useGridApiRef } from "@mui/x-data-grid";

const SPACING = 50;
const createEmptyComboFilters = () => ({
  status: "",
  carrier: "",
  centerNumber: "",
  studyCode: "",
});

export const ScreenOrders = (props) => {
  const apiRef = useGridApiRef();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const orders = useSelector((state) => state.order.orders);
  const isLoading = useSelector((state) => state.order.isLoadingOrders);
  const studyId = location.state?.studyId;
  const centerId = location.state?.centerId;
  const centerNumber = location.state?.centerNumber;
  const studyCode = location.state?.studyCode;
  const cdlUser = useSelector(
    (state) => state.user.user.organization === "CDL",
  );
  const adminUser = useSelector((state) => state.user.user.role === "admin");
  const centerUser = useSelector(
    (state) => state.user.user.organization === "Center",
  );
  const [selectedRows, setSelectedRows] = React.useState([]);
  const today = new Date();
  const [FSDialogOpen, setFSDialogOpen] = React.useState(false);
  const [isFirstSupply, setIsFirstSupply] = React.useState(false);
  const [selectedOrder, setSelectedOrder] = React.useState({});
  const updatedOrder = useSelector((state) => state.order.updatedOrder);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
  const [statusOptions, setStatusOptions] = React.useState([]);
  const [carrierOptions, setCarrierOptions] = React.useState([]);
  const [studyCodeOptions, setStudyCodeOptions] = React.useState([]);
  const [centerNumberOptions, setCenterNumberOptions] = React.useState([]);
  const [ordersWithFormattedDates, setOrdersWithFormattedDates] =
    React.useState([]);
  const [comboFilters, setComboFilters] = React.useState(
    createEmptyComboFilters,
  );
  const formattedDate = `${today.getFullYear()}-${
    today.getMonth() + 1
  }-${today.getDate()}`;
  let filename = `orders_${formattedDate}`;
  if (studyCode || centerNumber) {
    filename = `${studyCode ? studyCode + "_" : ""}${
      centerNumber ? centerNumber + "_" : ""
    }orders_${formattedDate}`;
  }

  const handleFSConfirm = () => {
    store.dispatch(updateOrder(selectedOrder.id, { isFirstSupply }));
    setFSDialogOpen(false);
    showSuccessAlert(t("orderUpdatedSuccessfully"));
  };

  function calculateProcessingTime(order) {
    if (!order.sendingDate || order.sendingDate === t("notSentYet")) {
      return "";
    }
    if (order.isFirstSupply) {
      return t("firstSupply");
    }
    const orderDate = moment(order.orderDate, "DD/MM/YYYY")
      .startOf("day")
      .toDate();
    const sendingDate = moment(order.sendingDate, "DD/MM/YYYY")
      .startOf("day")
      .toDate();

    if (sendingDate < orderDate) {
      return t("notPossible");
    }
    let workingDays = 0;
    for (
      let day = new Date(orderDate);
      day <= sendingDate;
      day.setDate(day.getDate() + 1)
    ) {
      if (!isWeekend(day)) {
        workingDays++;
      }
    }
    return workingDays > 0 ? workingDays - 1 : 0;
  }

  const updateComboFilter = React.useCallback((field, value) => {
    setComboFilters((currentFilters) => ({
      ...currentFilters,
      [field]: value || "",
    }));
  }, []);

  const activeFilterLabels = React.useMemo(() => {
    const labels = [];
    const addLabel = (label) => {
      if (label && !labels.includes(label)) {
        labels.push(label);
      }
    };

    if (comboFilters.studyCode) {
      addLabel(comboFilters.studyCode);
    }

    if (comboFilters.centerNumber) {
      addLabel(comboFilters.centerNumber);
    }

    if (comboFilters.status) {
      addLabel(comboFilters.status);
    }

    if (comboFilters.carrier) {
      addLabel(comboFilters.carrier);
    }

    return labels;
  }, [comboFilters]);

  const handleResetFilters = React.useCallback(() => {
    setComboFilters(createEmptyComboFilters());
    apiRef.current?.setFilterModel?.({ items: [] });
  }, [apiRef]);

  const baseColumns = React.useMemo(() => [
    {
      field: "orderNumber",
      headerName: t("orderNumber"),
      minWidth: 150,
      renderCell: (params) => (
        <div
          style={{
            cursor: "pointer",
            textDecoration: "none",
            color: "inherit",
          }}
          onClick={() => {
            navigate("/order", {
              state: {
                orderId: params.row.id,
                shipmentId: params.row.shipmentId,
                studyId: params.row.studyId,
                centerNumber: params.row.centerNumber,
                centerId: params.row.centerId,
                studyCode: cdlUser
                  ? params.row.studyCode
                  : params.row.sponsorCode,
              },
            });
          }}>
          {params.value}
        </div>
      ),
    },

    // Status column with custom filter
    {
      field: "status",
      headerName: t("status"),
      flex: 2,
      minWidth: 180,
      filterable: true,
      renderCell: (params) => {
        const value = String(params.value || "");
        const backOrderLabel = "⚠️ Back order";
        const isBackOrder = value.includes(backOrderLabel);

        // take first part before space
        const rawStatus = value.split(" ")[0];

        // translate using the raw key
        const translatedStatus = t(rawStatus, rawStatus);

        if (!isBackOrder) {
          return <span>{translatedStatus}</span>;
        }

        return (
          <span>
            {translatedStatus}
            <span
              style={{
                backgroundColor: "#FFF3B0",
                marginLeft: 4,
                padding: "1px 3px",
                borderRadius: 4,
                fontSize: "0.75rem",
              }}>
              {backOrderLabel}
            </span>
          </span>
        );
      },

      filterOperators: [
        {
          label: t("status"),
          value: "status",
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => value === filterItem.value;
          },
          InputComponent: (props) => (
            <CustomFilter
              label={t("status")}
              {...props}
              options={statusOptions}
              item={{
                ...props.item,
                value: props.item.value || comboFilters.status,
              }}
              applyValue={(nextItem) => {
                props.applyValue(nextItem);
                updateComboFilter("status", nextItem.value);
              }}
            />
          ),
        },
      ],
    },

    {
      field: "orderDate",
      type: "date",
      headerName: t("orderDate"),
      minWidth: 150,
      renderHeader: () => (
        <div style={{ whiteSpace: "normal", lineHeight: "normal" }}>
          {t("orderDate")}
          <br />
          <span style={{ fontSize: "x-small" }}>(DD/MM/YYYY)</span>
        </div>
      ),
      valueGetter: (params) => params.value,
      renderCell: (params) =>
        params.value ? moment(params.value).format("DD/MM/YYYY") : "",
    },
    {
      field: "sendingDate",
      type: "date",
      headerName: t("sendingDate"),
      minWidth: 150,
      renderHeader: () => (
        <div style={{ whiteSpace: "normal", lineHeight: "normal" }}>
          {t("sendingDate")}
          <br />
          <span style={{ fontSize: "x-small" }}>(DD/MM/YYYY)</span>{" "}
        </div>
      ),
      valueGetter: (params) => params.value,
      renderCell: (params) =>
        params.value
          ? moment(params.value).format("DD/MM/YYYY")
          : t("notSentYet"),
    },
    {
      field: "deliveryDate",
      type: "date",
      minWidth: 150,
      headerName: t("deliveryDate"),
      renderHeader: () => (
        <div style={{ whiteSpace: "normal", lineHeight: "normal" }}>
          {t("deliveryDate")}
          <br />
          <span style={{ fontSize: "x-small" }}>(DD/MM/YYYY)</span>{" "}
        </div>
      ),
      valueGetter: (params) => params.value,
      renderCell: (params) =>
        params.value
          ? moment(params.value).format("DD/MM/YYYY")
          : t("notDeliveredYet"),
    },
    {
      field: "centerValidatedDate",
      minWidth: 150,
      type: "date",
      headerName: t("centerValidatedDate"),
      renderHeader: () => (
        <div style={{ whiteSpace: "normal", lineHeight: "normal" }}>
          {t("centerValidatedDate")}
          <br />
          <span style={{ fontSize: "x-small" }}>(DD/MM/YYYY)</span>
        </div>
      ),
      valueGetter: (params) => params.value,
      renderCell: (params) =>
        params.value
          ? moment(params.value).format("DD/MM/YYYY")
          : t("notValidatedYet"),
    },
  ], [
    cdlUser,
    comboFilters.status,
    navigate,
    statusOptions,
    t,
    updateComboFilter,
  ]);
  const columns = React.useMemo(() => {
    const nextColumns = [...baseColumns];

    if (!centerUser) {
      nextColumns.push(
        {
          field: "processingTime",
          headerName: t("processingTime"),
          minWidth: 130,
          renderHeader: () => (
            <div style={{ whiteSpace: "normal", lineHeight: "normal" }}>
              {t("processingTime")}
            </div>
          ),
          valueGetter: (params) => calculateProcessingTime(params.row),
        },
        {
          field: "carrier",
          headerName: t("carrier"),
          minWidth: 130,
          filterable: true,
          renderCell: (params) => params.value,
          filterOperators: [
            {
              label: t("carrier"),
              value: "carrier",
              getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) {
                  return null;
                }
                return ({ value }) => value === filterItem.value;
              },
              InputComponent: (props) => (
                <CustomFilter
                  label={t("carrier")}
                  options={carrierOptions}
                  {...props}
                  item={{
                    ...props.item,
                    value: props.item.value || comboFilters.carrier,
                  }}
                  applyValue={(nextItem) => {
                    props.applyValue(nextItem);
                    updateComboFilter("carrier", nextItem.value);
                  }}
                />
              ),
            },
          ],
        },
        {
          field: "packingSlip",
          headerName: t("airwaybill"),
          headerAlign: "left",
          minWidth: 130,
        },
        { field: "comment", headerName: t("comment"), minWidth: 300 },
      );
    }

    if (!centerId) {
      nextColumns.unshift({
        field: "centerNumber",
        headerName: t("center"),
        flex: 1,
        minWidth: 100,
        filterable: true,
        renderCell: (params) => params.value,
        filterOperators: [
          {
            label: t("center"),
            value: "centerNumber",
            getApplyFilterFn: (filterItem) => {
              if (!filterItem.value) {
                return null;
              }
              return ({ value }) => value === filterItem.value;
            },
            InputComponent: (props) => (
              <CustomFilter
                {...props}
                options={centerNumberOptions}
                item={{
                  ...props.item,
                  value: props.item.value || comboFilters.centerNumber,
                }}
                applyValue={(nextItem) => {
                  props.applyValue(nextItem);
                  updateComboFilter("centerNumber", nextItem.value);
                }}
              />
            ),
          },
        ],
      });

      nextColumns.splice(1, 0, {
        field: "centerProtocol",
        headerName: `${t("center")} ${t("protocol")}`,
        minWidth: 170,
        renderCell: (params) => params.value,
      });
    }

    if (!studyId && cdlUser) {
      nextColumns.unshift({
        field: "studyCode",
        headerName: t("studyCode"),
        flex: 1,
        minWidth: 110,
        filterable: true,
        renderCell: (params) => params.value,
        filterOperators: [
          {
            label: t("studyCode"),
            value: "studyCode",
            getApplyFilterFn: (filterItem) => {
              if (!filterItem.value) {
                return null;
              }
              return ({ value }) => value === filterItem.value;
            },
            InputComponent: (props) => (
              <CustomFilter
                {...props}
                options={studyCodeOptions}
                item={{
                  ...props.item,
                  value: props.item.value || comboFilters.studyCode,
                }}
                applyValue={(nextItem) => {
                  props.applyValue(nextItem);
                  updateComboFilter("studyCode", nextItem.value);
                }}
              />
            ),
          },
        ],
      });
    }

    return nextColumns;
  }, [
    baseColumns,
    carrierOptions,
    cdlUser,
    centerId,
    centerNumberOptions,
    centerUser,
    comboFilters.carrier,
    comboFilters.centerNumber,
    comboFilters.studyCode,
    studyCodeOptions,
    studyId,
    t,
    updateComboFilter,
  ]);

  const getStatusIndex = (status) => {
    const dynamicStatusOrder = adminUser
      ? [
          "placed",
          "confirmed",
          "shipped",
          "shipped 1/2",
          "partially shipped",
          "delivered",
          "delivered 1/2",
          "received",
          "received 1/2",
          "partially delivered",
          "cancelled",
          "lost",
        ]
      : [
          "confirmed",
          "placed",
          "shipped",
          "shipped 1/2",
          "partially shipped",
          "delivered",
          "delivered 1/2",
          "received",
          "received 1/2",
          "partially delivered",
          "cancelled",
          "lost",
        ];

    return dynamicStatusOrder.indexOf(status);
  };

  React.useEffect(() => {
    if (centerId) {
      store.dispatch(getOrdersbyCenter(centerId));
    } else if (studyId) {
      store.dispatch(getOrdersbyStudy(studyId));
    } else {
      store.dispatch(getOrders());
    }
  }, [studyId, centerId, updatedOrder]);

  const isBackOrderViaOrderStatus = (order) => {
    if (
      !order ||
      !Array.isArray(order.shipments) ||
      order.shipments.length === 0
    )
      return false;
    return order.status.includes("1/2");
  };

  const renderOrderStatus = (order) => {
    if (isBackOrderViaOrderStatus(order)) {
      return order.status + " ⚠️ Back order";
    }
    return order.status;
  };

  React.useEffect(() => {
    if (orders.length > 0) {
      const ordersWithShipments = orders.flatMap((order) => {
        if (order.shipments.length > 0) {
          return order.shipments.map((shipment, index) => {
            return {
              key: `${order.id}-${shipment?.id ?? index}`,
              studyCode: order.Center?.Study?.cdlStudyCode,
              sponsorCode: order.Center?.Study?.sponsorStudyCode,
              id: order.id,
              shipmentId: shipment?.id ?? null,
              studyId: order.Center?.Study?.id,
              centerId: order.centerId,
              orderNumber: order.orderNumber,
              status: renderOrderStatus(order),
              totalQuantity: order.totalQuantity,
              isFirstSupply: order.isFirstSupply,
              orderDate: new Date(order.orderDate),
              centerNumber: order.Center.centerNumber,
              centerProtocol:
                order.Center?.Protocols?.map(
                  (protocol) => protocol.reference,
                ).join(", ") || "",
              sendingDate: shipment ? new Date(shipment.sendingDate) : null,
              deliveryDate: shipment?.deliveryDate
                ? new Date(shipment.deliveryDate)
                : null,
              centerValidatedDate: shipment?.confirmedDeliveryDate
                ? new Date(shipment.confirmedDeliveryDate)
                : null,
              carrier: shipment?.carrier || null,
              packingSlip: shipment?.packingSlip || null,
              comment: order.comment,
            };
          });
        } else {
          return [
            {
              key: `${order.id}-0`,
              studyCode: order.Center?.Study?.cdlStudyCode,
              sponsorCode: order.Center?.Study?.sponsorStudyCode,
              id: order.id,
              shipmentId: null,
              studyId: order.Center?.Study?.id,
              centerId: order.centerId,
              orderNumber: order.orderNumber,
              status: order.status,
              isFirstSupply: order.isFirstSupply,
              orderDate: new Date(order.orderDate),
              centerNumber: order.Center.centerNumber,
              centerProtocol:
                order.Center?.Protocols?.map(
                  (protocol) => protocol.reference,
                ).join(", ") || "",
              sendingDate: null,
              deliveryDate: null,
              centerValidatedDate: null,
              carrier: null,
              packingSlip: null,
              comment: order.comment,
            },
          ];
        }
      });

      if (cdlUser || adminUser) {
        ordersWithShipments.sort(
          (a, b) => getStatusIndex(a.status) - getStatusIndex(b.status),
        );
      }
      setOrdersWithFormattedDates(ordersWithShipments);
    } else {
      setOrdersWithFormattedDates([]);
    }
  }, [orders, cdlUser, adminUser]);

  React.useEffect(() => {
    if (ordersWithFormattedDates.length > 0) {
      setStatusOptions(getUniqueOptions(ordersWithFormattedDates, "status"));
      setCarrierOptions(getUniqueOptions(ordersWithFormattedDates, "carrier"));
      setStudyCodeOptions(
        getUniqueOptions(ordersWithFormattedDates, "studyCode"),
      );
      setCenterNumberOptions(
        getUniqueOptions(ordersWithFormattedDates, "centerNumber"),
      );
    } else {
      setStatusOptions([]);
      setCarrierOptions([]);
      setStudyCodeOptions([]);
      setCenterNumberOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersWithFormattedDates]);

  const displayedOrders = React.useMemo(() => {
    return ordersWithFormattedDates.filter((order) => {
      if (comboFilters.studyCode && order.studyCode !== comboFilters.studyCode) {
        return false;
      }

      if (
        comboFilters.centerNumber &&
        order.centerNumber !== comboFilters.centerNumber
      ) {
        return false;
      }

      if (comboFilters.status && order.status !== comboFilters.status) {
        return false;
      }

      if (comboFilters.carrier && order.carrier !== comboFilters.carrier) {
        return false;
      }

      return true;
    });
  }, [comboFilters, ordersWithFormattedDates]);

  React.useEffect(() => {
    const visibleKeys = new Set(displayedOrders.map((row) => row.key));
    setSelectedRowKeys((currentKeys) =>
      currentKeys.filter((rowKey) => visibleKeys.has(rowKey)),
    );
  }, [displayedOrders]);

  React.useEffect(() => {
    const nextSelectedRows = displayedOrders
      .filter((order) => selectedRowKeys.includes(order.key))
      .map((order) => {
        const filteredOrder = {};
        columns.forEach((column) => {
          if (column.field === "processingTime") {
            filteredOrder[column.headerName] = calculateProcessingTime(order);
          } else {
            filteredOrder[column.headerName] = order[column.field];
          }
        });
        return filteredOrder;
      });

    setSelectedRows(nextSelectedRows);
  }, [columns, displayedOrders, selectedRowKeys]);

  return (
    <Grid item xs={12}>
      <Paper sx={{ display: "flex", flexDirection: "column" }}>
        <div style={{ width: "100%", height: "90vh", position: "relative" }}>
          <LocalizedDataGrid
            apiRef={apiRef}
            slots={{
              noRowsOverlay: NoRowOverlay,
              loadingOverlay: LinearProgress,
            }}
            rows={displayedOrders}
            columns={columns}
            loading={isLoading}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 50 },
              },
            }}
            getRowId={(row) => row.key}
            rowHeight={40}
            pageSizeOptions={[20, 50, 100]}
            checkboxSelection
            onRowSelectionModelChange={(newSelection) => {
              setSelectedRowKeys(newSelection);
            }}
            onCellDoubleClick={(params) => {
              if (params.field === "processingTime" && cdlUser) {
                setIsFirstSupply(params.row.isFirstSupply);
                setFSDialogOpen(true);
                setSelectedOrder(params.row);
              }
            }}
          />
          {activeFilterLabels.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                left: 16,
                bottom: selectedRows.length > 0 ? 58 : 10,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                maxWidth: "55%",
                color: "text.secondary",
                fontSize: "0.875rem",
                zIndex: 2,
              }}>
              <IconButton
                aria-label="Reset filters"
                onClick={handleResetFilters}
                size="small"
                sx={{ p: 0.25 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                Filters: {activeFilterLabels.join(", ")}
              </Box>
            </Box>
          )}
          <FirstSupplyDialog
            open={FSDialogOpen}
            handleClose={() => setFSDialogOpen(false)}
            isFirstSupply={isFirstSupply}
            setIsFirstSupply={setIsFirstSupply}
            handleFSConfirm={handleFSConfirm}
          />
        </div>

        {selectedRows.length > 0 && (
          <CSVDownloader
            data={selectedRows}
            filename={filename}
            name={t("selectedOrders")}
          />
        )}
      </Paper>

      {centerId && (
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => {
            navigate("/orders/new", {
              state: { centerId, studyId, centerNumber, studyCode },
            });
          }}
          style={{ position: "absolute", right: SPACING, bottom: SPACING }}>
          <AddIcon />
        </Fab>
      )}
    </Grid>
  );
};
